import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { EQM1020_R01Dto } from './Dto_EQM1020_R01';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

interface ChartProps {
    data: EQM1020_R01Dto[];
}

export const Chart_EQM1020_R01: React.FC<ChartProps> = ({ data }) => { // data 을 매개변수로 받아옴
    const toTenth = (s: string) => {
        const n = parseFloat(s);
        if (isNaN(n)) return s;
        return (n / 10).toFixed(1);
    };
    const toTenthNum = (n: number | null): number | null => {
        if (n === null || isNaN(n)) return null;
        return parseFloat((n / 10).toFixed(1));
    };
    React.useEffect(() => {
        if (data.length > 0) {
            const uniqueIds: number[] = []; // 숫자타입의 배열 선언
            const idSet = new Set<number>(); // 숫자타입의 셋 객체 선언 (중복 제거)
            data.forEach(d => { // 데이터를 순회하면서 셋에 해당 값이 없으면 추가
                if (!idSet.has(d.plcaddressid)) { // 셋에 해당 plcaddressid 값이 없으면 추가
                    idSet.add(d.plcaddressid); // 셋에 추가
                    uniqueIds.push(d.plcaddressid); // 배열에 추가
                }
            });

            uniqueIds.forEach(id => {
                const items = data.filter(d => d.plcaddressid === id);
                const firstItem = items[0];
            });
            
            // 시간별 그룹화 및 최대값 테스트
            const hourGroups = new Map<string, Map<number, number[]>>();
            data.forEach(item => {
                const time = item.logMinute;
                let hourKey = '';
                if (time.includes(' ')) {
                    const timePart = time.split(' ')[1];
                    const hour = timePart.split(':')[0];
                    hourKey = `${hour.padStart(2, '0')}:00`;
                } else if (time.includes(':')) {
                    const hour = time.split(':')[0];
                    hourKey = `${hour.padStart(2, '0')}:00`;
                } else {
                    hourKey = time;
                }
                
                if (!hourGroups.has(hourKey)) {
                    hourGroups.set(hourKey, new Map());
                }
                const hourData = hourGroups.get(hourKey)!;
                if (!hourData.has(item.plcaddressid)) {
                    hourData.set(item.plcaddressid, []);
                }
                hourData.get(item.plcaddressid)!.push(parseFloat(item.value));
            });
            
            Array.from(hourGroups.entries()).sort().forEach(([hour, hourData]) => {
                const maxValues: { [key: number]: number } = {};
                hourData.forEach((values, id) => {
                    maxValues[id] = Math.max(...values);
                });
            });
        }
    }, [data]);
    
    const { labels, currentActualData, voltageActualData, latestData, currentYMax, voltageYMax, currentYMin, voltageYMin, maxTimeData } = useMemo(() => {
        // 24시간을 10분 단위로 전개한 라벨 생성 (00:00 ~ 23:50)
        const labels: string[] = [];
        for (let h = 7; h <= 21; h++) {
            for (let m = 0; m < 60; m += 10) {
                labels.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
            }
        }

        const minuteGroups = new Map<string, Map<number, { values: number[]; times: string[] }>>();
        data.forEach(item => {
            const time = item.logMinute;
            let hour = '';
            let minute = '';
            if (time.includes(' ')) {
                const t = time.split(' ')[1];
                hour = t.split(':')[0];
                minute = t.split(':')[1];
            } else if (time.includes(':')) {
                hour = time.split(':')[0];
                minute = time.split(':')[1];
            } else {
                hour = time; minute = '00';
            }
            const minuteGroup = Math.floor(parseInt(minute, 10) / 10) * 10;
            const key = `${hour.padStart(2, '0')}:${minuteGroup.toString().padStart(2, '0')}`;
            if (!minuteGroups.has(key)) minuteGroups.set(key, new Map());
            const byPlc = minuteGroups.get(key)!;
            if (!byPlc.has(item.plcaddressid)) byPlc.set(item.plcaddressid, { values: [], times: [] });
            const rec = byPlc.get(item.plcaddressid)!;
            rec.values.push(parseFloat(item.value));
            rec.times.push(time);
        });

        // 설정값 사용 안함
        // const currentSet: (number|null)[] = [];
        const currentActual: (number|null)[] = [];
        // const voltageSet: (number|null)[] = [];
        const voltageActual: (number|null)[] = [];
        const maxTimeInfo: { [key: string]: { [key: number]: string } } = {};

        const getMaxValueAndTime = (data?: { values: number[]; times: string[] }) => {
            if (!data || data.values.length === 0) return { maxValue: null as number|null, maxTime: '' };
            const maxValue = Math.max(...data.values);
            const maxIndex = data.values.indexOf(maxValue);
            const maxTime = data.times[maxIndex];
            return { maxValue, maxTime };
        };

        labels.forEach(key => {
            const byPlc = minuteGroups.get(key);
            // const svAmp = getMaxValueAndTime(byPlc?.get(375)); // 설정값 사용 안함
            const pvAmp = getMaxValueAndTime(byPlc?.get(377));
            // const svVolt = getMaxValueAndTime(byPlc?.get(374)); // 설정값 사용 안함
            const pvVolt = getMaxValueAndTime(byPlc?.get(376));

            currentActual.push(pvAmp.maxValue);
            voltageActual.push(pvVolt.maxValue);

            if (!maxTimeInfo[key]) maxTimeInfo[key] = {};
            // if (svAmp.maxTime) maxTimeInfo[key][375] = svAmp.maxTime; // 설정값 시간 기록 제외
            if (pvAmp.maxTime) maxTimeInfo[key][377] = pvAmp.maxTime;
            // if (svVolt.maxTime) maxTimeInfo[key][374] = svVolt.maxTime; // 설정값 시간 기록 제외
            if (pvVolt.maxTime) maxTimeInfo[key][376] = pvVolt.maxTime;
        });

        // 가장 최근 데이터 존재 라벨 찾기
        let latestLabel = '';
        for (let i = labels.length - 1; i >= 0; i--) {
            const key = labels[i];
            if (minuteGroups.has(key)) { latestLabel = key; break; }
        }

        // latestLabel(10분 버킷) 내에서 실제 원본 시각들 중 가장 최근 분:초를 찾아 HH:mm로 표시
        const getHHmm = (t: string) => {
            if (!t) return '';
            if (t.includes(' ')) {
                const timePart = t.split(' ')[1]; // HH:mm:ss
                return timePart.substring(0, 5);
            }
            // t가 HH:mm 또는 HH:mm:ss로 들어올 수 있음
            return t.substring(0, 5);
        };

        let latestExactHHmm = '';
        if (latestLabel) {
            const byPlcAtLatest = minuteGroups.get(latestLabel);
            if (byPlcAtLatest) {
                const allTimes: string[] = [];
                byPlcAtLatest.forEach(group => {
                    allTimes.push(...group.times);
                });
                if (allTimes.length > 0) {
                    // 문자열 비교가 날짜 문자열에도 잘 동작하도록, HH:mm:ss 또는 yyyy-MM-dd HH:mm:ss 모두에서 가장 큰 것을 선택
                    const sorted = allTimes.slice().sort();
                    const maxTime = sorted[sorted.length - 1];
                    latestExactHHmm = getHHmm(maxTime);
                }
            }
        }

        const latest = {
            time: latestExactHHmm || latestLabel || '',
            // 설정값 표시 안함
            currentSet: '0',
            currentActual: (currentActual[labels.indexOf(latestLabel)] ?? 0)?.toString() ?? '0',
            voltageSet: '0',
            voltageActual: (voltageActual[labels.indexOf(latestLabel)] ?? 0)?.toString() ?? '0',
        };

        const toNum = (v: number|null) => (v === null || isNaN(v) ? undefined : v);
        // 설정값 제외하고 범위 계산
        const allCurrentData = [...currentActual.map(toNum)].filter((v): v is number => v !== undefined);
        const allVoltageData = [...voltageActual.map(toNum)].filter((v): v is number => v !== undefined);
        const currentMax = allCurrentData.length > 0 ? Math.max(...allCurrentData) : 0;
        const voltageMax = allVoltageData.length > 0 ? Math.max(...allVoltageData) : 0;
        const currentMin = allCurrentData.length > 0 ? Math.min(...allCurrentData) : 0;
        const voltageMin = allVoltageData.length > 0 ? Math.min(...allVoltageData) : 0;
        const currentYMax = currentMax > 0 ? Math.ceil(currentMax * 1.1) : 0;
        const voltageYMax = voltageMax > 0 ? Math.ceil(voltageMax * 1.1) : 0;
        const currentYMin = currentMin > 0 ? Math.floor(currentMin * 0.9) : 0;
        const voltageYMin = voltageMin > 0 ? Math.floor(voltageMin * 0.9) : 0;

        // 표시용: 전류만 1/10 스케일 적용
        const currentDisplay = currentActual.map(toTenthNum);
        const displayCurrentYMax = toTenthNum(currentYMax) ?? 0;
        const displayCurrentYMin = toTenthNum(currentYMin) ?? 0;

        return {
            labels,
            // currentSetData: currentSet, // 설정값 반환 안함
            currentActualData: currentDisplay,
            // voltageSetData: voltageSet, // 설정값 반환 안함
            voltageActualData: voltageActual,
            latestData: latest,
            currentYMax: displayCurrentYMax,
            voltageYMax,
            currentYMin: displayCurrentYMin,
            voltageYMin,
            maxTimeData: maxTimeInfo,
        };
    }, [data]);

    // 전류 차트 옵션
    const currentChartOptions = useMemo(() => ({
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
            title: {
                display: true,
                text: '전압 VOLTAGE (V)'
            },
            legend: {
                position: 'top' as const,
            },
            tooltip: {
                enabled: true,
                mode: 'index' as const,
                intersect: false,
                callbacks: {
                    title: function(context: any) {
                        const dataIndex = context[0].dataIndex;
                        const label = context[0].label;
                        
                        // 원본 데이터에서 해당 시간의 실제 분 정보 찾기
                        const originalTime = data.find(item => {
                            let timeKey = '';
                            if (item.logMinute.includes(' ')) {
                                const timePart = item.logMinute.split(' ')[1];
                                timeKey = timePart.substring(0, 5);
                            } else if (item.logMinute.includes(':')) {
                                timeKey = item.logMinute.substring(0, 5);
                            } else {
                                timeKey = item.logMinute;
                            }
                            return timeKey === label;
                        })?.logMinute;
                        
                        // 실제 시간:분:초 형태로 표시
                        if (originalTime) {
                            if (originalTime.includes(' ')) {
                                return originalTime.split(' ')[1]; // "16:01:30"
                            } else {
                                return originalTime; // "16:01:30"
                            }
                        }
                        
                        return label; // 기본값
                    },
                    label: function(context: any) {
                        const dataIndex = context.dataIndex;
                        const label = context.label;
                        const datasetLabel = context.dataset.label;
                        const value = context.parsed.y;
                        
                        // 최대값 발생 시간 정보 가져오기
                        const hourKey = label;
                        let maxTime = '';
                        
                        if (datasetLabel === '설정') {
                            maxTime = maxTimeData[hourKey]?.[375] || '';
                        } else if (datasetLabel === '현재') {
                            maxTime = maxTimeData[hourKey]?.[377] || '';
                        }
                        
                        // 시간 정보 포맷팅
                        let timeDisplay = '';
                        if (maxTime) {
                            if (maxTime.includes(' ')) {
                                timeDisplay = maxTime.split(' ')[1]; // "16:01:30"
                            } else {
                                timeDisplay = maxTime; // "16:01:30"
                            }
                        }
                        
                        // 최대값 발생 시간 표시
                        if (timeDisplay && timeDisplay !== hourKey + ':00') {
                            return `${datasetLabel}: ${Number(value).toFixed(1)} (${timeDisplay}) `;
                        } else {
                            return `${datasetLabel}: ${Number(value).toFixed(1)}`;
                        }
                    }
                }
            }
        },
        scales: {
            x: {
                ticks: {
                    maxTicksLimit: 15
                },
                title: {
                    display: true,
                    text: '시간'
                }
            },
            y: {
                beginAtZero: false,
                min: currentYMin,
                max: currentYMax,
                title: {
                    display: true,
                    text: 'Volt (V)'
                }
            },
        },
    }), [currentYMax, currentYMin, data, maxTimeData]);

    // 전압 차트 옵션
    const voltageChartOptions = useMemo(() => ({
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
            title: {
                display: true,
                text: '전류 CURRENT (A)'
            },
            legend: {
                position: 'top' as const,
            },
            tooltip: {
                enabled: true,
                mode: 'index' as const,
                intersect: false,
                callbacks: {
                    title: function(context: any) {
                        const dataIndex = context[0].dataIndex;
                        const label = context[0].label;
                        
                        // 원본 데이터에서 해당 시간의 실제 분 정보 찾기
                        const originalTime = data.find(item => {
                            let timeKey = '';
                            if (item.logMinute.includes(' ')) {
                                const timePart = item.logMinute.split(' ')[1];
                                timeKey = timePart.substring(0, 5);
                            } else if (item.logMinute.includes(':')) {
                                timeKey = item.logMinute.substring(0, 5);
                            } else {
                                timeKey = item.logMinute;
                            }
                            return timeKey === label;
                        })?.logMinute;
                        
                        // 실제 시간:분:초 형태로 표시
                        if (originalTime) {
                            if (originalTime.includes(' ')) {
                                return originalTime.split(' ')[1]; // "16:01:30"
                            } else {
                                return originalTime; // "16:01:30"
                            }
                        }
                        
                        return label; // 기본값
                    },
                    label: function(context: any) {
                        const dataIndex = context.dataIndex;
                        const label = context.label;
                        const datasetLabel = context.dataset.label;
                        const value = context.parsed.y;
                        
                        // 최대값 발생 시간 정보 가져오기
                        const hourKey = label;
                        let maxTime = '';
                        
                        if (datasetLabel === '설정') {
                            maxTime = maxTimeData[hourKey]?.[374] || '';
                        } else if (datasetLabel === '현재') {
                            maxTime = maxTimeData[hourKey]?.[376] || '';
                        }
                        
                        // 시간 정보 포맷팅
                        let timeDisplay = '';
                        if (maxTime) {
                            if (maxTime.includes(' ')) {
                                timeDisplay = maxTime.split(' ')[1]; // "16:01:30"
                            } else {
                                timeDisplay = maxTime; // "16:01:30"
                            }
                        }
                        
                        // 최대값 발생 시간 표시
                        if (timeDisplay && timeDisplay !== hourKey + ':00') {
                            return `${datasetLabel}: ${value} (${timeDisplay})`;
                        } else {
                            return `${datasetLabel}: ${value}`;
                        }
                    }
                }
            }
        },
        scales: {
            x: {
                ticks: {
                    maxTicksLimit: 15
                                },
                title: {
                    display: true,
                    text: '시간'
                }
            },
            y: {
                beginAtZero: false,
                min: voltageYMin,
                max: voltageYMax,
                title: {
                    display: true,
                    text: 'Ampere (A)'
                }
            },
        },
    }), [voltageYMax, voltageYMin, data, maxTimeData]);

    // 전류 차트 데이터
    const currentChartData = {
        labels: labels,
        datasets: [
            // {
            //     label: '설정',
            //     data: currentSetData,
            //     borderColor: 'rgb(59, 130, 246)',  // Blue
            //     backgroundColor: 'rgba(59, 130, 246, 0.1)',
            //     tension: 0.1,
            //     pointRadius: 3,
            //     pointHoverRadius: 5,
            //     pointBackgroundColor: 'rgb(59, 130, 246)',
            //     pointBorderColor: 'rgb(59, 130, 246)',
            //     pointBorderWidth: 1,
            // },
            {
                label: '현재',
                data: currentActualData,
                borderColor: 'rgb(251, 146, 60)',  // Orange
                backgroundColor: 'rgba(251, 146, 60, 0.1)',
                tension: 0.1,
                pointRadius: 3,
                pointHoverRadius: 5,
                pointBackgroundColor: 'rgb(251, 146, 60)',
                pointBorderColor: 'rgb(251, 146, 60)',
                pointBorderWidth: 1,
            },
        ],
    };

    // 전압 차트 데이터
    const voltageChartData = {
        labels: labels,
        datasets: [
            // {
            //     label: '설정',
            //     data: voltageSetData,
            //     borderColor: 'rgb(59, 130, 246)',  // Blue
            //     backgroundColor: 'rgba(59, 130, 246, 0.1)',
            //     tension: 0.1,
            //     pointRadius: 3,
            //     pointHoverRadius: 5,
            //     pointBackgroundColor: 'rgb(59, 130, 246)',
            //     pointBorderColor: 'rgb(59, 130, 246)',
            //     pointBorderWidth: 1,
            // },
            {
                label: '현재',
                data: voltageActualData,
                borderColor: 'rgb(251, 146, 60)',  // Orange
                backgroundColor: 'rgba(251, 146, 60, 0.1)',
                tension: 0.1,
                pointRadius: 3,
                pointHoverRadius: 5,
                pointBackgroundColor: 'rgb(251, 146, 60)',
                pointBorderColor: 'rgb(251, 146, 60)',
                pointBorderWidth: 1,
            },
        ],
    };

    return (
        <div className="w-full h-full flex flex-col gap-2">
            {/* 최종 데이터 표시 */}
            <div className="flex gap-8 items-center justify-center bg-white border border-gray-300  pb-1 ">
                <div className="flex flex-col items-start">
                    <p className="text-2xl text-gray-600 font-bold ">최종시간</p>
                    <p className="text-xl ">
                        <span className="inline-flex items-center px-5 justify-center bg-white text-gray-600 ">
                            {latestData.time || '-'}
                        </span>
                    </p>
                </div>
                <div className="flex flex-col items-center ">
                <p className="text-2xl text-gray-600 font-bold pl-9">
                전압 <span className="font-normal text-base">VOLTAGE</span>
                </p>
                    <p className="text-xl ">
                        <span className="inline-flex items-center justify-center px-2 w-[80px] h-[32px]    bg-white text-black border-[1px] border-gray-300 ">
                        <span className="text-orange-500">{toTenth(latestData.currentActual)}</span>
                            <span className="text-gray-600 ml-1">V</span>
                        </span>
                    </p>
                </div>
                <div className="flex flex-col items-start pl-2">
                <p className="text-2xl text-gray-600 font-bold ">
                전류 <span className="font-normal text-base">CURRENT</span>
                </p>
                    <p className="text-xl ">
                        <span className="inline-flex items-center justify-center    px-2 w-[80px] h-[32px]  bg-white text-black border-[1px] border-gray-300 ">
                        <span className="text-orange-500">{latestData.voltageActual}</span>
                            <span className="text-gray-600 ml-1">A</span>
                        </span>
                    </p>
                </div>
            </div>

            {/* 차트 그리드 */}
            <div className="flex-1 grid grid-cols-1 gap-2 border border-gray-300 ">
                {/* 전류 차트 */}
                <div className="bg-white rounded-lg shadow">
                    <div className="h-[300px]">
                        <Line data={currentChartData} options={currentChartOptions} />
                    </div>
                </div>

                {/* 전압 차트 */}
                <div className="bg-white  rounded-lg shadow">
                    <div className="h-[300px]">
                        <Line data={voltageChartData} options={voltageChartOptions} />
                    </div>
                </div>
            </div>
        </div>
    );
};
